import React, { useState, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import './HeroScene.css';
import PreparedStickerCard from './PreparedStickerCard';
import ClothedEyeLoader from './ClothedEyeLoader';
import databaseService from '../services/databaseService';
import intelligentLoadingService from '../services/intelligentLoadingService';
import randomStickerService from '../services/randomStickerService';
import heroSceneSettings from '../services/heroSceneSettings';
import backgroundImageService from '../services/backgroundImageService';
import MaterialIcon from './MaterialIcon';

import logger from '../utils/logger.js';
const HeroScene = ({
  onStickerClick = () => {},
  allStickers = [],
  isLoadingStickers = false,
  useSmartLoading = true,
  excludedListingIds = [],
}) => {
  const [loading, setLoading] = useState(true);
  const [heroStickers, setHeroStickers] = useState([]);
  const [heroReady, setHeroReady] = useState(false); // Track when 3 SVGs are preloaded
  const [backgroundImage, setBackgroundImage] = useState('');

  // Component mount/unmount tracking
  useEffect(() => {
    logger.log('HeroScene mounted');
    return () => logger.log('HeroScene unmounting');
  }, []);

  // Animation state management
  const [cardAnimationStates, setCardAnimationStates] = useState({});
  const animationTimeouts = useRef([]);

  // Machine twitch state management
  const [activeMachinePart, setActiveMachinePart] = useState(null); // 'left', 'middle', 'right', or null
  const [machineTwitchState, setMachineTwitchState] = useState('idle'); // 'idle', 'pre-tease', 'active'

  // Machine awareness state during background transitions
  const [machineAwarenessState, setMachineAwarenessState] = useState('idle'); // 'idle', 'reactive'

  // New attached sticker system
  const [attachedStickers, setAttachedStickers] = useState({}); // { left: cardData, center: cardData, right: cardData }
  const [isGenerating, setIsGenerating] = useState(false);
  const wiggleTimeouts = useRef([]);

  // Background transition state
  const [backgroundTransitionState, setBackgroundTransitionState] = useState('normal'); // 'normal', 'fading-to-dark', 'dark', 'fading-to-new'
  const backgroundTransitionTimeouts = useRef([]);

  // Machine breakdown state for fun "fall apart" animation
  const [refreshCount, setRefreshCount] = useState(0);
  const [machineIsBroken, setMachineIsBroken] = useState(false);
  const machineBreakdownTimeouts = useRef([]);

  // Red circle port coordinates (aligned with actual exit points on cropped images)
  const RED_CIRCLE_PORTS = [
    { id: 'left', x: 27, y: 75 }, // Left arm: positioned over the actual port
    { id: 'center', x: 46, y: 60 }, // Middle: positioned over the actual port
    { id: 'right', x: 64, y: 75 }, // Right arm: positioned over the actual port
  ];

  // Animation constants
  const ANIMATION_PHASES = {
    HIDDEN: 'hidden',
    GROWING: 'growing',
    WIGGLING: 'wiggling',
    FALLING: 'falling',
    FINAL: 'final',
    ATTACHED: 'attached', // New phase for stickers attached to machine
  };

  const ANIMATION_TIMING = {
    GROWTH_DURATION: 1000, // 1.0s - mechanical extrusion
    WIGGLE_DURATION: 200, // 0.2s - disconnect wiggle
    FALL_DURATION: 400, // 0.4s - faster gravity fall
    TOTAL_DURATION: 1600, // 1.6s total per card
    PRE_TEASE_DURATION: 300, // 0.3s - pre-tease twitch
    MACHINE_TWITCH_DURATION: 1500, // 1.5s - extended twitch during growth + wiggle
  };

  // Set random background on component mount
  useEffect(() => {
    const randomBackground = backgroundImageService.getRandomBackground();
    setBackgroundImage(randomBackground);
  }, []);

  // Smart loading or fallback to provided stickers
  useEffect(() => {
    const loadHeroData = async () => {
      if (useSmartLoading) {
        try {
          // Hero scene using SMART loading
          setLoading(true);

          const smartData = await intelligentLoadingService.loadCriticalFirst();
          const heroStickers = smartData.heroStickers || [];

          setHeroStickers(heroStickers);
          setLoading(false);

          // Hero scene loaded via smart loading
          // Smart loading stats debug reduced
        } catch (error) {
          console.error('❌ Smart loading failed, loading fresh data from database:', error);
          // Fall back to fresh database query for stickers with SVG paths and MICRO size
          try {
            const freshStickers = await databaseService.getUnifiedStickers({
              hasSvgPath: true,
              publicVisible: true,
              limit: 200,
            });

            const validHeroStickers = freshStickers.data.filter((sticker) => {
              if (!sticker.available_sizes || sticker.available_sizes.length === 0) return false;
              // Look for any size that starts with 'MICRO' (e.g., 'MICRO Rider', 'MICRO Dragon', etc.)
              return sticker.available_sizes.some((size) =>
                String(size).toUpperCase().startsWith('MICRO')
              );
            });

            logger.log(
              `✅ Loaded ${validHeroStickers.length} hero stickers from fresh database query`
            );
            // Debug: Check the first few stickers to see their structure
            if (validHeroStickers.length > 0) {
              logger.log('🔍 First hero sticker structure:', validHeroStickers[0]);
            }
            setHeroStickers(validHeroStickers);
            setLoading(false);
          } catch (dbError) {
            console.error('❌ Database fallback also failed:', dbError);
            // Last resort: use provided stickers
            setHeroStickers(allStickers);
            setLoading(false);
            if (allStickers.length > 0) {
              const REVEAL_START_DELAY = 1500;
              setTimeout(() => {
                setHeroReady(true);
                logger.log('Hero ready - starting reveal animation (fallback path)');
              }, REVEAL_START_DELAY);
            }
          }
        }
      } else {
        // Original logic: use provided stickers
        if (isLoadingStickers) {
          setLoading(true);
          return;
        }

        if (allStickers && allStickers.length > 0) {
          setHeroStickers(allStickers);
          setLoading(false);
          // Hero scene using provided data
        }
      }
    };

    loadHeroData();
  }, [allStickers, isLoadingStickers, useSmartLoading]);

  // Animation Functions
  const getPortForCardIndex = useCallback((cardIndex) => {
    return RED_CIRCLE_PORTS[cardIndex % RED_CIRCLE_PORTS.length];
  }, []);

  const stopAnimationSequence = useCallback(() => {
    // Clear all timeouts
    animationTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    animationTimeouts.current = [];

    // Reset animation state
    setCardAnimationStates({});

    // Reset machine twitch state
    setActiveMachinePart(null);
    setMachineTwitchState('idle');

    // Reset machine awareness state
    setMachineAwarenessState('idle');
  }, []);

  // Machine twitch functions
  const getMachinePartForCardIndex = useCallback(
    (cardIndex) => {
      const port = getPortForCardIndex(cardIndex);
      if (port.x <= 35) return 'left'; // Left arm (x: 20-35)
      if (port.x >= 60) return 'right'; // Right arm (x: 60+)
      return 'middle'; // Center (x: 36-59)
    },
    [getPortForCardIndex]
  );

  const startMachineTwitch = useCallback(
    (cardIndex) => {
      const machinePart = getMachinePartForCardIndex(cardIndex);
      logger.log(`TWITCH: Card ${cardIndex} -> ${machinePart}`);
      setActiveMachinePart(machinePart);

      // Start pre-tease twitch
      setMachineTwitchState('pre-tease');

      // Transition to active twitch after pre-tease
      const activeTimeout = setTimeout(() => {
        setMachineTwitchState('active');
      }, ANIMATION_TIMING.PRE_TEASE_DURATION);

      // Stop twitching after growth completes
      const stopTimeout = setTimeout(() => {
        setMachineTwitchState('idle');
        setActiveMachinePart(null);
      }, ANIMATION_TIMING.PRE_TEASE_DURATION + ANIMATION_TIMING.MACHINE_TWITCH_DURATION);

      animationTimeouts.current.push(activeTimeout, stopTimeout);
    },
    [getMachinePartForCardIndex]
  );

  // Cleanup animation timeouts on unmount
  useEffect(() => {
    return () => {
      stopAnimationSequence();
      // Clear background transition timeouts
      backgroundTransitionTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, [stopAnimationSequence]);

  // Start wiggle animation for attached stickers
  const startWiggleAnimation = useCallback((cardId) => {
    const wiggleTimeout = setTimeout(
      () => {
        setCardAnimationStates((prev) => ({
          ...prev,
          [cardId]: {
            ...prev[cardId],
            phase: ANIMATION_PHASES.WIGGLING,
          },
        }));

        // Return to attached state after wiggle
        const returnTimeout = setTimeout(() => {
          setCardAnimationStates((prev) => ({
            ...prev,
            [cardId]: {
              ...prev[cardId],
              phase: ANIMATION_PHASES.ATTACHED,
            },
          }));

          // Schedule next wiggle
          startWiggleAnimation(cardId);
        }, 200);

        wiggleTimeouts.current.push(returnTimeout);
      },
      Math.random() * 1000 + 2000
    ); // Random between 2-3 seconds

    wiggleTimeouts.current.push(wiggleTimeout);
  }, []);

  // Handle "New" button click
  const handleNewSticker = useCallback(
    async (portId) => {
      if (isGenerating || backgroundTransitionState !== 'normal') return;

      const currentSticker = attachedStickers[portId];
      if (!currentSticker) return;

      // Start fall animation immediately
      setCardAnimationStates((prev) => ({
        ...prev,
        [currentSticker.id]: {
          ...prev[currentSticker.id],
          phase: ANIMATION_PHASES.FALLING,
        },
      }));

      // Remove sticker after fall
      setTimeout(() => {
        setAttachedStickers((prev) => {
          const newStickers = { ...prev };
          delete newStickers[portId];
          return newStickers;
        });
        setCardAnimationStates((prev) => {
          const newStates = { ...prev };
          delete newStates[currentSticker.id];
          return newStates;
        });
      }, 500);

      // Generate new sticker using randomStickerService after 1 second pause
      setTimeout(async () => {
        try {
          const newStickerData = await randomStickerService.getRandomSticker();

          const newSticker = {
            id: newStickerData.id,
            title: newStickerData.title,
            svgPath: newStickerData.svgPath,
            size: newStickerData.size,
            colorIndex: newStickerData.colorIndex,
            width: newStickerData.width,
            height: newStickerData.height,
            portId,
            phase: ANIMATION_PHASES.HIDDEN,
            stickerData: newStickerData.stickerData,
            isTBMDITS: newStickerData.isTBMDITS || false,
          };

          setAttachedStickers((prev) => ({
            ...prev,
            [portId]: newSticker,
          }));

          setCardAnimationStates((prev) => ({
            ...prev,
            [newSticker.id]: {
              phase: ANIMATION_PHASES.HIDDEN,
              portId,
              wiggleCount: Math.floor(Math.random() * 4) + 2,
            },
          }));

          // Start generation sequence immediately
          setTimeout(() => {
            startMachineTwitch(['left', 'center', 'right'].indexOf(portId));

            setTimeout(() => {
              setCardAnimationStates((prev) => ({
                ...prev,
                [newSticker.id]: {
                  ...prev[newSticker.id],
                  phase: ANIMATION_PHASES.GROWING,
                },
              }));

              setTimeout(() => {
                setCardAnimationStates((prev) => ({
                  ...prev,
                  [newSticker.id]: {
                    ...prev[newSticker.id],
                    phase: ANIMATION_PHASES.WIGGLING,
                  },
                }));

                setTimeout(() => {
                  setCardAnimationStates((prev) => ({
                    ...prev,
                    [newSticker.id]: {
                      ...prev[newSticker.id],
                      phase: ANIMATION_PHASES.ATTACHED,
                    },
                  }));

                  startWiggleAnimation(newSticker.id);
                }, 200);
              }, 1000);
            }, 100);
          }, 100);
        } catch (error) {
          console.error('Failed to generate new sticker:', error);
        }
      }, 1000);
    },
    [
      isGenerating,
      backgroundTransitionState,
      attachedStickers,
      startMachineTwitch,
      startWiggleAnimation,
    ]
  );

  // Machine breakdown animation handler
  const handleMachineBreakdown = useCallback(async () => {
    setIsGenerating(true);
    logger.log(`😢 Machine falling apart...`);

    // Start sticker falling animations immediately
    const currentStickers = Object.values(attachedStickers);
    currentStickers.forEach((sticker) => {
      setCardAnimationStates((prev) => ({
        ...prev,
        [sticker.id]: {
          ...prev[sticker.id],
          phase: ANIMATION_PHASES.FALLING,
        },
      }));
    });

    // Set machine to breakdown state - this will trigger CSS animations
    setMachineAwarenessState('breakdown');

    // Clear stickers after they fall
    setTimeout(() => {
      setAttachedStickers({});
      setCardAnimationStates({});
    }, 2000);

    // Machine stays broken - no auto-repair
    setTimeout(() => {
      setIsGenerating(false); // Allow user to try clicking again, but machine stays broken
      logger.log(`💔 Machine is broken and staying broken...`);
    }, 3000);
  }, [attachedStickers]);

  const handleRefreshHero = useCallback(async () => {
    if (isGenerating || backgroundTransitionState !== 'normal') return;

    // If machine is already broken, don't allow refresh
    if (machineIsBroken) {
      logger.log(`🚫 Machine is broken! Can't refresh.`);
      return;
    }

    // Increment refresh count and check for breakdown
    const newRefreshCount = refreshCount + 1;
    setRefreshCount(newRefreshCount);

    // Debug mode for local development
    const isLocalDev =
      window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    // Breakdown logic
    let triggerBreakdown = false;
    if (isLocalDev) {
      // Debug: Break on 2nd click for local development
      triggerBreakdown = newRefreshCount >= 2;
    } else {
      // Production: Random chance after 4+ refreshes
      if (newRefreshCount > 4 && !machineIsBroken) {
        const breakdownChance = Math.min(0.15 + (newRefreshCount - 4) * 0.15, 0.8);
        triggerBreakdown = Math.random() < breakdownChance;
      }
    }

    if (triggerBreakdown) {
      const mode = isLocalDev ? 'DEBUG' : 'RANDOM';
      logger.log(`💥 MACHINE BREAKDOWN TRIGGERED! (${mode} - Refresh ${newRefreshCount})`);
      setMachineIsBroken(true);
      // Skip normal refresh and trigger breakdown animation
      handleMachineBreakdown();
      return;
    }

    // Immediately set isGenerating to prevent useEffect race condition
    setIsGenerating(true);

    // Stop current animation sequence
    stopAnimationSequence();

    // Clear any existing background transition timeouts
    backgroundTransitionTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    backgroundTransitionTimeouts.current = [];

    // Start sticker falling animations first
    const currentStickers = Object.values(attachedStickers);
    currentStickers.forEach((sticker) => {
      setCardAnimationStates((prev) => ({
        ...prev,
        [sticker.id]: {
          ...prev[sticker.id],
          phase: ANIMATION_PHASES.FALLING,
        },
      }));
    });

    // Start background transition: normal → dark (slower timing)
    setBackgroundTransitionState('fading-to-dark');

    // Activate machine awareness - make machine reactive to the background change
    setMachineAwarenessState('reactive');

    // After 500ms, set to full dark and change background
    const darkTimeout = setTimeout(() => {
      setBackgroundTransitionState('dark');

      // Change background to a new random one during dark phase
      const newRandomBackground = backgroundImageService.getRandomBackground();
      setBackgroundImage(newRandomBackground);

      // After another 500ms, fade back to normal
      const normalTimeout = setTimeout(() => {
        setBackgroundTransitionState('fading-to-new');

        // After final 500ms, complete transition
        const completeTimeout = setTimeout(() => {
          setBackgroundTransitionState('normal');
          // Machine calms down when background transition is complete
          setMachineAwarenessState('idle');
        }, 500);

        backgroundTransitionTimeouts.current.push(completeTimeout);
      }, 500);

      backgroundTransitionTimeouts.current.push(normalTimeout);
    }, 500);

    backgroundTransitionTimeouts.current.push(darkTimeout);

    // Clear stickers after falling animation completes (400ms fall + 100ms cleanup)
    // BUT keep isGenerating=true to prevent any other sticker generation during background transition
    setTimeout(() => {
      setAttachedStickers({});
      setCardAnimationStates({});
      // DON'T set isGenerating(false) here - keep it true until new generation starts
    }, 500);

    // Generate new attached stickers ONLY after background is FULLY restored AND machine has calmed down
    setTimeout(async () => {
      const ports = ['left', 'center', 'right'];

      try {
        // Generate stickers using service - get next in global variety sequence
        const stickerPromises = ports.map(() => randomStickerService.getRandomSticker());
        const generatedStickers = await Promise.all(stickerPromises);

        const newAttachedStickers = {};
        const newAnimationStates = {};

        ports.forEach((portId, index) => {
          const stickerData = generatedStickers[index];

          const sticker = {
            id: stickerData.id,
            title: stickerData.title,
            svgPath: stickerData.svgPath,
            size: stickerData.size,
            colorIndex: stickerData.colorIndex,
            width: stickerData.width,
            height: stickerData.height,
            portId,
            phase: ANIMATION_PHASES.HIDDEN,
            stickerData: stickerData.stickerData,
          };

          newAttachedStickers[portId] = sticker;
          newAnimationStates[sticker.id] = {
            phase: ANIMATION_PHASES.HIDDEN,
            portId,
            wiggleCount: Math.floor(Math.random() * 4) + 2,
          };
        });

        setAttachedStickers(newAttachedStickers);
        setCardAnimationStates((prev) => ({ ...prev, ...newAnimationStates }));

        // Start generation sequence with delays
        // Add buffer delay to ensure all CSS animations have settled
        ports.forEach((portId, index) => {
          const sticker = newAttachedStickers[portId];
          if (sticker) {
            const delay = index * 1000 + 300; // Add 300ms buffer to prevent conflicts with CSS transitions

            setTimeout(() => {
              startMachineTwitch(index);

              setTimeout(() => {
                setCardAnimationStates((prev) => ({
                  ...prev,
                  [sticker.id]: {
                    ...prev[sticker.id],
                    phase: ANIMATION_PHASES.GROWING,
                  },
                }));

                setTimeout(() => {
                  setCardAnimationStates((prev) => ({
                    ...prev,
                    [sticker.id]: {
                      ...prev[sticker.id],
                      phase: ANIMATION_PHASES.WIGGLING,
                    },
                  }));

                  setTimeout(() => {
                    setCardAnimationStates((prev) => ({
                      ...prev,
                      [sticker.id]: {
                        ...prev[sticker.id],
                        phase: ANIMATION_PHASES.ATTACHED,
                      },
                    }));

                    startWiggleAnimation(sticker.id);
                  }, 200);
                }, 1000);
              }, 100);
            }, delay);
          }
        });

        setTimeout(() => {
          setIsGenerating(false);
        }, 3000);
      } catch (error) {
        console.error('Failed to refresh hero stickers:', error);
        setIsGenerating(false);
      }
    }, 2000); // Wait for background to be FULLY restored, machine to calm down, THEN generate stickers
  }, [
    isGenerating,
    backgroundTransitionState,
    refreshCount,
    machineIsBroken,
    stopAnimationSequence,
    startMachineTwitch,
    startWiggleAnimation,
    handleMachineBreakdown,
  ]);

  // NEW ATTACHED STICKER SYSTEM FUNCTIONS

  /**
   * Process raw sticker data from API into format needed by Hero Scene
   * Uses randomStickerService's proven dimension calculator
   *
   * @param {Object} rawSticker - Raw sticker data from database API
   * @returns {Object} Processed sticker with size, width, height fields
   */
  const processStickerForHero = (rawSticker) => {
    // Use randomStickerService's calculateDimensions - proven to work in recycle
    const { width, height } = randomStickerService.calculateDimensions(rawSticker, 'MICRO');

    logger.log(`📐 Processed sticker ${rawSticker.etsyListingId}: ${rawSticker.title}`, {
      size: 'MICRO',
      width,
      height,
    });

    return {
      id: rawSticker.etsyListingId || rawSticker.id,
      etsyListingId: rawSticker.etsyListingId,
      title: rawSticker.title,
      svgPath: rawSticker.svgPath || rawSticker.svg_path,
      size: 'MICRO',
      width,
      height,
      colorIndex: 0, // Default to first color
      stickerData: rawSticker, // Preserve full raw data for other uses
    };
  };

  // Initialize attached stickers immediately (skip slow hero data load)
  useEffect(() => {
    // FAST PATH: Don't wait for loading or heroStickers, just initialize directly
    if (
      Object.keys(attachedStickers).length === 0 &&
      backgroundTransitionState === 'normal' &&
      !isGenerating &&
      !heroReady
    ) {
      // Stop the old animation system first
      stopAnimationSequence();

      if (!isGenerating) {
        const initializeStickers = async () => {
          setIsGenerating(true);

          try {
            // Step 1: Select which stickers to show (don't attach yet)
            const generatedStickers = [];

            // TBMDITS data (not a real listing)
            const tbmditsData = {
              id: 'logo-first-design',
              title: 'The Best Micro Decals In The System',
              svgPath: '/assets/svgs/clothedEye_BMDITS.svg',
              size: 'MEDIUM',
              colorIndex: 0, // Always black
              width: 200,
              height: 120,
              isFirstDesign: true,
              isTBMDITS: true, // Special flag to identify this sticker
            };

            // CRITICAL FIX: Mark logo as shown BEFORE getting real stickers
            // This prevents randomStickerService from returning TBMDITS for left/right arms
            randomStickerService.logoShownThisSession = true;
            randomStickerService.saveSessionTracking();

            // CACHE CHECK: Prevent React StrictMode from selecting different stickers on remount
            let realStickers = []; // Declare outside to avoid scoping issues

            if (!window.__heroStickerCache) {
              logger.log('🚀 First mount - loading hero stickers...');

              // Check if specific listings are configured
              const settings = await heroSceneSettings.getListingIds();

              logger.log('🔍 Hero scene settings check:', settings);

              if (settings.leftListingId && settings.rightListingId) {
                // CONFIGURED MODE: Query database directly for these 2 specific IDs
                logger.log(
                  `⚡ CONFIGURED MODE: Loading Left=${settings.leftListingId}, Right=${settings.rightListingId}`
                );

                try {
                  // Fetch the 2 specific stickers directly from database by ID
                  const isDev =
                    typeof window !== 'undefined' &&
                    (window.location.hostname === 'localhost' || window.location.port === '8888');
                  const base = isDev ? '/.netlify/functions' : '/api/index.php';
                  const [leftResponse, rightResponse] = await Promise.all([
                    fetch(`${base}/unified-stickers/${settings.leftListingId}`).then((r) =>
                      r.json()
                    ),
                    fetch(`${base}/unified-stickers/${settings.rightListingId}`).then((r) =>
                      r.json()
                    ),
                  ]);

                  const leftData = leftResponse?.data;
                  const rightData = rightResponse?.data;

                  logger.log('📦 Raw configured stickers loaded from API:', {
                    left: {
                      id: leftData?.etsyListingId,
                      hasPath: !!(leftData?.svgPath || leftData?.svg_path),
                      hasDimensions: !!leftData?.dimensions,
                      hasSizes: !!leftData?.sizes,
                    },
                    right: {
                      id: rightData?.etsyListingId,
                      hasPath: !!(rightData?.svgPath || rightData?.svg_path),
                      hasDimensions: !!rightData?.dimensions,
                      hasSizes: !!rightData?.sizes,
                    },
                  });

                  if (leftData && rightData) {
                    // Process raw sticker data to extract size, width, height
                    const processedLeft = processStickerForHero(leftData);
                    const processedRight = processStickerForHero(rightData);

                    realStickers = [processedLeft, processedRight];

                    logger.log('✅ Processed 2 configured stickers with dimensions:', {
                      left: {
                        id: processedLeft.id,
                        size: processedLeft.size,
                        width: processedLeft.width,
                        height: processedLeft.height,
                      },
                      right: {
                        id: processedRight.id,
                        size: processedRight.size,
                        width: processedRight.width,
                        height: processedRight.height,
                      },
                    });
                  } else {
                    throw new Error(`Failed to load configured stickers`);
                  }
                } catch (error) {
                  logger.warn(
                    '⚠️ Failed to load configured listings, falling back to random:',
                    error
                  );
                  // Fall through to random selection
                }
              }

              // RANDOM MODE: If not configured or configured listings failed
              if (realStickers.length === 0) {
                logger.log('🎲 Using random selection...');

                // Query for stickers with SVG paths
                const heroStickersRaw = await databaseService.getUnifiedStickers({
                  hasSvgPath: true,
                  publicVisible: true,
                  limit: 100, // Get pool of 100
                });

                // Pick 2 random from the pool
                const pool = (heroStickersRaw.data || []).filter((s) => s.svgPath || s.svg_path);
                const shuffled = pool.sort(() => Math.random() - 0.5);
                realStickers = shuffled.slice(0, 2);

                if (realStickers.length < 2) {
                  throw new Error('Not enough stickers with SVG paths found');
                }

                logger.log(`✅ Selected 2 random stickers from pool of ${pool.length}`);
              }

              // Cache for StrictMode remount
              window.__heroStickerCache = realStickers;
            } else {
              logger.log('🔄 Using cached hero stickers (StrictMode remount)');
              realStickers = window.__heroStickerCache;
            }

            // Arrange in proper order: left (real), center (TBMDITS), right (real)
            generatedStickers[0] = realStickers[0]; // left port
            generatedStickers[1] = tbmditsData; // center port
            generatedStickers[2] = realStickers[1]; // right port

            logger.log('🎬 Stickers selected - expanding hero scene');

            // Attach and show immediately
            const ports = ['left', 'center', 'right'];
            const newAttachedStickers = {};
            const newAnimationStates = {};

            ports.forEach((portId, index) => {
              const stickerData = generatedStickers[index];

              const sticker = {
                id: stickerData.id,
                title: stickerData.title,
                svgPath: stickerData.svgPath,
                size: stickerData.size,
                colorIndex: stickerData.colorIndex,
                width: stickerData.width,
                height: stickerData.height,
                portId,
                phase: ANIMATION_PHASES.HIDDEN,
                stickerData: stickerData.stickerData,
                isTBMDITS: stickerData.isTBMDITS || false, // Preserve TBMDITS flag
              };

              newAttachedStickers[portId] = sticker;
              newAnimationStates[sticker.id] = {
                phase: ANIMATION_PHASES.HIDDEN,
                portId,
                wiggleCount: Math.floor(Math.random() * 4) + 2,
              };
            });

            setAttachedStickers(newAttachedStickers);
            setCardAnimationStates((prev) => ({ ...prev, ...newAnimationStates }));

            // Force browser to paint collapsed state before triggering reveal
            // Double requestAnimationFrame ensures the collapsed state (height: 0px) renders first
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                // Now browser has painted the collapsed state
                // Wait additional delay before starting reveal animation
                const REVEAL_START_DELAY = 100; // 100ms delay before animation starts
                const HERO_ANIMATION_DURATION = 2000; // 2s animation duration (matches HeroScene.css line 41)

                setTimeout(() => {
                  setHeroReady(true); // Triggers CSS animation: height 0px → 600px
                  logger.log('Hero ready - starting reveal animation');

                  // Dispatch event when hero animation COMPLETES
                  setTimeout(() => {
                    const event = new CustomEvent('heroAnimationComplete');
                    window.dispatchEvent(event);
                    logger.log('Hero animation complete - parallax can now reveal');
                  }, HERO_ANIMATION_DURATION);
                }, REVEAL_START_DELAY);
              });
            });

            // Start generation sequence with delays - CENTER FIRST (index 1), then left (0), then right (2)
            const generationOrder = [1, 0, 2]; // center, left, right

            generationOrder.forEach((portIndex, sequenceIndex) => {
              const portId = ports[portIndex];
              const sticker = newAttachedStickers[portId];
              if (sticker) {
                const delay = sequenceIndex * 1000;

                setTimeout(() => {
                  startMachineTwitch(portIndex);

                  setTimeout(() => {
                    setCardAnimationStates((prev) => ({
                      ...prev,
                      [sticker.id]: {
                        ...prev[sticker.id],
                        phase: ANIMATION_PHASES.GROWING,
                      },
                    }));

                    setTimeout(() => {
                      setCardAnimationStates((prev) => ({
                        ...prev,
                        [sticker.id]: {
                          ...prev[sticker.id],
                          phase: ANIMATION_PHASES.WIGGLING,
                        },
                      }));

                      setTimeout(() => {
                        setCardAnimationStates((prev) => ({
                          ...prev,
                          [sticker.id]: {
                            ...prev[sticker.id],
                            phase: ANIMATION_PHASES.ATTACHED,
                          },
                        }));

                        startWiggleAnimation(sticker.id);
                      }, 200);
                    }, 1000);
                  }, 100);
                }, delay);
              }
            });

            setTimeout(() => {
              setIsGenerating(false);
            }, 3000);
          } catch (error) {
            console.error('Failed to initialize stickers:', error);
            setIsGenerating(false);
            // Show anyway with delay for animation
            const REVEAL_START_DELAY = 1500;
            setTimeout(() => {
              setHeroReady(true);
              logger.log('Hero ready - starting reveal animation (error fallback)');
            }, REVEAL_START_DELAY);
          }
        };

        initializeStickers();
      }
    }
  }, [
    attachedStickers,
    isGenerating,
    heroReady,
    backgroundTransitionState,
    stopAnimationSequence,
    startMachineTwitch,
    startWiggleAnimation,
  ]);

  // Cleanup wiggle timeouts on unmount
  useEffect(() => {
    return () => {
      wiggleTimeouts.current.forEach((timeout) => clearTimeout(timeout));
    };
  }, []);

  // REMOVE THE BLOCKING LOADER - Show scene immediately with watermarks
  // Let images load progressively in the background

  return (
    <section className={`hero hero-full-section ${heroReady ? 'hero-ready' : 'hero-loading'}`}>
      {/* Loading bar (only visible when collapsed) */}
      {!heroReady && (
        <div className="hero-loading-bar">
          <div className="loading-progress-fill"></div>
        </div>
      )}

      {/* Hero content (always visible, revealed by height expansion) */}
      <div className="container-professional">
        <div className="hero-content hero-full-content">
          {/* Full hero area with attached sticker system */}
          <div className="hero-full-scatter">
            {/* New Background Image - Full area coverage */}
            <div className="hero-background-image">
              <img
                src={backgroundImage || '/assets/images/gears/hero-bkg.jpg'}
                alt="Hero Background"
                className={`hero-bkg-img hero-bkg-${backgroundTransitionState}`}
                onError={() => console.error('Failed to load hero background image')}
              />
            </div>

            {/* Modular Sticker Machine Assembly */}
            <div className={`hero-machine-assembly hero-machine-${backgroundTransitionState}`}>
              {/* Core machine part - behind everything */}
              <div
                className={`hero-machine-core ${machineAwarenessState === 'reactive' ? 'machine-aware' : ''} ${machineAwarenessState === 'breakdown' ? 'machine-breakdown' : ''}`}
              >
                <img
                  src="/assets/images/gears/CE-sticker-machine-core.png"
                  alt="Sticker Machine Core"
                  className="hero-machine-core-img"
                  onError={() => console.error('Failed to load machine core image')}
                />
              </div>

              {/* Left machine part */}
              <div
                className={`hero-machine-left ${activeMachinePart === 'left' ? `machine-${machineTwitchState}` : ''} ${machineAwarenessState === 'reactive' ? 'machine-aware' : ''} ${machineAwarenessState === 'breakdown' ? 'machine-breakdown' : ''}`}
              >
                <img
                  src="/assets/images/gears/CE-sticker-machine-left.png"
                  alt="Sticker Machine Left"
                  className="hero-machine-part-img"
                  onError={() => console.error('Failed to load machine left image')}
                />

                {/* Left arm attached sticker */}
                {attachedStickers.left &&
                  (() => {
                    const sticker = attachedStickers.left;
                    const animationState = cardAnimationStates[sticker.id];
                    const animationPhase = animationState?.phase || ANIMATION_PHASES.HIDDEN;

                    if (animationPhase === ANIMATION_PHASES.HIDDEN) {
                      return null;
                    }

                    // For falling phase, move to bottom of scene (outside arm)
                    if (animationPhase === ANIMATION_PHASES.FALLING) {
                      return null; // Will be handled by separate falling container
                    }

                    // Get animation class
                    let animationClass = `hero-card-${animationPhase}`;

                    // Add random wiggle class for wiggle phase
                    if (animationPhase === ANIMATION_PHASES.WIGGLING) {
                      const wiggleCount = animationState?.wiggleCount || 2;
                      animationClass += ` randomWiggle${wiggleCount}`;
                    }

                    // Check if sticker is fully generated and ready for interaction
                    const isFullyGenerated = animationPhase === ANIMATION_PHASES.ATTACHED;

                    return (
                      <div
                        key={`attached-left-${sticker.id}`}
                        className={`left-arm-sticker hero-card-animated ${animationClass}`}
                      >
                        <PreparedStickerCard
                          id={sticker.id}
                          svgPath={sticker.svgPath}
                          title={sticker.title}
                          size={sticker.size}
                          colorIndex={sticker.colorIndex}
                          width={sticker.width}
                          height={sticker.height}
                          onStickerClick={isFullyGenerated ? onStickerClick : null}
                          showInteractionButtons={isFullyGenerated}
                          onNewSticker={() => handleNewSticker('left')}
                          portId="left"
                          stickerData={sticker.stickerData}
                        />
                      </div>
                    );
                  })()}
              </div>

              {/* Middle machine part */}
              <div
                className={`hero-machine-middle ${activeMachinePart === 'middle' ? `machine-${machineTwitchState}` : ''} ${machineAwarenessState === 'reactive' ? 'machine-aware' : ''} ${machineAwarenessState === 'breakdown' ? 'machine-breakdown' : ''}`}
              >
                <img
                  src="/assets/images/gears/CE-sticker-machine-middle.png"
                  alt="Sticker Machine Middle"
                  className="hero-machine-part-img"
                  onError={() => console.error('Failed to load machine middle image')}
                />

                {/* Center arm attached sticker */}
                {attachedStickers.center &&
                  (() => {
                    const sticker = attachedStickers.center;
                    const animationState = cardAnimationStates[sticker.id];
                    const animationPhase = animationState?.phase || ANIMATION_PHASES.HIDDEN;

                    if (animationPhase === ANIMATION_PHASES.HIDDEN) {
                      return null;
                    }

                    // For falling phase, move to bottom of scene (outside arm)
                    if (animationPhase === ANIMATION_PHASES.FALLING) {
                      return null; // Will be handled by separate falling container
                    }

                    // Get animation class
                    let animationClass = `hero-card-${animationPhase}`;

                    // Add random wiggle class for wiggle phase
                    if (animationPhase === ANIMATION_PHASES.WIGGLING) {
                      const wiggleCount = animationState?.wiggleCount || 2;
                      animationClass += ` randomWiggle${wiggleCount}`;
                    }

                    // Check if sticker is fully generated and ready for interaction
                    const isFullyGenerated = animationPhase === ANIMATION_PHASES.ATTACHED;

                    // SPECIAL HANDLING for TBMDITS: clicking it should trigger recycle, not open modal
                    const handleCenterClick = sticker.isTBMDITS
                      ? () => handleNewSticker('center') // Recycle animation for TBMDITS
                      : onStickerClick; // Normal modal for real stickers

                    return (
                      <div
                        key={`attached-center-${sticker.id}`}
                        className={`center-arm-sticker hero-card-animated ${animationClass}`}
                      >
                        <PreparedStickerCard
                          id={sticker.id}
                          svgPath={sticker.svgPath}
                          title={sticker.title}
                          size={sticker.size}
                          colorIndex={sticker.colorIndex}
                          width={sticker.width}
                          height={sticker.height}
                          onStickerClick={isFullyGenerated ? handleCenterClick : null}
                          showInteractionButtons={isFullyGenerated && !sticker.isTBMDITS}
                          onNewSticker={() => handleNewSticker('center')}
                          portId="center"
                          stickerData={sticker.stickerData}
                        />
                      </div>
                    );
                  })()}
              </div>

              {/* Right machine part */}
              <div
                className={`hero-machine-right ${activeMachinePart === 'right' ? `machine-${machineTwitchState}` : ''} ${machineAwarenessState === 'reactive' ? 'machine-aware' : ''} ${machineAwarenessState === 'breakdown' ? 'machine-breakdown' : ''}`}
                onClick={() =>
                  logger.log(
                    `RIGHT ARM: activeMachinePart=${activeMachinePart}, twitchState=${machineTwitchState}`
                  )
                }
              >
                <img
                  src="/assets/images/gears/CE-sticker-machine-right.png"
                  alt="Sticker Machine Right"
                  className="hero-machine-part-img"
                  onError={() => console.error('Failed to load machine right image')}
                />

                {/* Right arm attached sticker */}
                {attachedStickers.right &&
                  (() => {
                    const sticker = attachedStickers.right;
                    const animationState = cardAnimationStates[sticker.id];
                    const animationPhase = animationState?.phase || ANIMATION_PHASES.HIDDEN;

                    if (animationPhase === ANIMATION_PHASES.HIDDEN) {
                      return null;
                    }

                    // For falling phase, move to bottom of scene (outside arm)
                    if (animationPhase === ANIMATION_PHASES.FALLING) {
                      return null; // Will be handled by separate falling container
                    }

                    // Get animation class
                    let animationClass = `hero-card-${animationPhase}`;

                    // Add random wiggle class for wiggle phase
                    if (animationPhase === ANIMATION_PHASES.WIGGLING) {
                      const wiggleCount = animationState?.wiggleCount || 2;
                      animationClass += ` randomWiggle${wiggleCount}`;
                    }

                    // Check if sticker is fully generated and ready for interaction
                    const isFullyGenerated = animationPhase === ANIMATION_PHASES.ATTACHED;

                    return (
                      <div
                        key={`attached-right-${sticker.id}`}
                        className={`right-arm-sticker hero-card-animated ${animationClass}`}
                      >
                        <PreparedStickerCard
                          id={sticker.id}
                          svgPath={sticker.svgPath}
                          title={sticker.title}
                          size={sticker.size}
                          colorIndex={sticker.colorIndex}
                          width={sticker.width}
                          height={sticker.height}
                          onStickerClick={isFullyGenerated ? onStickerClick : null}
                          showInteractionButtons={isFullyGenerated}
                          onNewSticker={() => handleNewSticker('right')}
                          portId="right"
                          stickerData={sticker.stickerData}
                        />
                      </div>
                    );
                  })()}
              </div>

              {/* Falling Stickers Container - Only for stickers in falling phase */}
              <div className="falling-stickers-container">
                {Object.entries(attachedStickers).map(([portId, sticker]) => {
                  const animationState = cardAnimationStates[sticker.id];
                  const animationPhase = animationState?.phase || ANIMATION_PHASES.HIDDEN;
                  const port = RED_CIRCLE_PORTS.find((p) => p.id === portId);

                  // Only render falling stickers here
                  if (!port || animationPhase !== ANIMATION_PHASES.FALLING) {
                    return null;
                  }

                  // Calculate falling position - start from arm position, move to bottom
                  const cardStyle = {
                    left: `${port.x}%`,
                    top: '90%',
                    transform: 'translate(-50%, -50%)',
                    position: 'absolute',
                    zIndex: 4,
                  };

                  // Get animation class
                  const animationClass = `hero-card-falling`;

                  return (
                    <div
                      key={`falling-${sticker.id}`}
                      className={`hero-card-animated ${animationClass}`}
                      style={cardStyle}
                    >
                      <PreparedStickerCard
                        id={sticker.id}
                        svgPath={sticker.svgPath}
                        title={sticker.title}
                        size={sticker.size}
                        colorIndex={sticker.colorIndex}
                        width={sticker.width}
                        height={sticker.height}
                        onStickerClick={null}
                        showInteractionButtons={false}
                        onNewSticker={() => handleNewSticker(portId)}
                        portId={portId}
                        stickerData={sticker.stickerData}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* TBMDITS Logo Watermark - Original overlay */}
            <div className="hero-watermark">
              <img
                src="/assets/svgs/clothedEye_BMDITS.svg"
                alt="The Best Micro Decals In The System"
                className={`watermark-logo ${loading ? 'loading-pulse' : ''}`}
                onError={() => console.error('Failed to load watermark image')}
              />
              {loading && Object.keys(attachedStickers).length === 0 && (
                <div className="hero-loading-overlay">
                  <ClothedEyeLoader size="medium" showTagline={false} showLogo={false} />
                </div>
              )}
            </div>

            {/* Refresh button (lower-right) */}
            <div className="hero-scene-button" aria-live="polite">
              <button
                type="button"
                className="easy-button"
                onClick={handleRefreshHero}
                aria-label="Refresh hero stickers and background"
                title="Refresh hero stickers and background"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: 'rgba(0,0,0,0.5)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                }}
              >
                <MaterialIcon icon="refresh" size={18} />
                <span style={{ fontSize: 12 }}>Refresh</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// PropTypes validation
HeroScene.propTypes = {
  onStickerClick: PropTypes.func,
  allStickers: PropTypes.array,
  isLoadingStickers: PropTypes.bool,
  useSmartLoading: PropTypes.bool,
  excludedListingIds: PropTypes.array,
};

export default HeroScene;
